import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { map, Observable, startWith } from 'rxjs';
import { Currency } from '../../../../../../currencies/models/Currency';
import { CurrenciesService } from '../../../../../../currencies/services/currencies.service';
import { VerificationPackageDetail, VerificationPackage } from '../../../../../../verification-packages/models/VerificationPackage';

@Component({
  selector: 'app-org-assign-verif-package-dialog',
  templateUrl: './org-assign-verif-package-dialog.component.html',
  styleUrls: ['./org-assign-verif-package-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatIcon,
    MatDialogModule,
    MatButtonModule
  ]
})
export class OrgAssignVerifPackageDialogComponent implements OnInit, AfterViewInit {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  form!: FormGroup;
  currencies!: Currency[];
  selectedVerificationPackage!: any;
  availableVerificationPackages: VerificationPackageDetail[] = [];

  verifPackageControl = new FormControl('', Validators.required);
  filteredVerifPackages!: Observable<any[]>;
  verificationPackageSelect!: VerificationPackage | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<OrgAssignVerifPackageDialogComponent>,
    private formBuilder: FormBuilder,
    private currenciesService: CurrenciesService
  ) { }

  ngOnInit(): void {
    this.availableVerificationPackages = this.data.availableVerificationPackages;
    this.selectedVerificationPackage = this.data.verificationPackageToUpdate;
    this.loadData();
    this.initForm();

    if (this.selectedVerificationPackage) {
      this.loadFromVerificationPackage(this.selectedVerificationPackage);
    }
  }

  ngAfterViewInit() {
    this.filteredVerifPackages = this.verifPackageControl.valueChanges.pipe(
      startWith(''),
      map(value => this._verifPackageFilter(value || '')),
    );

    this.autocompleteTrigger.panelClosingActions.subscribe((e) => {      
      if (!(e && e.source)) {
        this.verifPackageControl.setValue('');
        this.autocompleteTrigger.closePanel();
      }
    });
  }

  private _verifPackageFilter(value: string): VerificationPackageDetail[] {
    return this.availableVerificationPackages.filter(verifPackage => verifPackage.name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  loadFromVerificationPackage(verifPackage: any) {
    this.verifPackageControl.setValue(verifPackage);
    this.form.controls['price'].setValue(verifPackage.price_number);
    this.form.controls['currency_id'].setValue(this.data.verificationPackageDetail.currency_id);
  }

  async loadData() {
    this.verifPackageControl.setValue('');
    const currenciesResponse = await this.currenciesService.getCurrencies();
    this.currencies = currenciesResponse;
  }

  initForm() {
    this.form = this.formBuilder.group({
      price: [''],
      currency_id: ['', Validators.required]
    });
  }

  onVerifPackageSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.verificationPackageSelect = event.option.value;
  }

  submitAssignVerificationPackage() {
    if (this.selectedVerificationPackage) {
      let payload = this.buildUpdateVerificationPackagePayload();
      this.dialogRef.close(payload);
      this.resetForm();
    } else {
      let payload = this.buildCreateVerificationPackagePayload();
      this.dialogRef.close(payload);
      this.resetForm();
    }
  }

  buildCreateVerificationPackagePayload() {
    let isPrimary: boolean = false;
    if (this.data.firstVerificationPackageBoolean === true) {
      isPrimary = true;
    }
    let createPayload = {
      organization_id: this.data.organizationId,
      price: this.form.get('price')?.value ? this.form.get('price')?.value : 0,
      is_primary: isPrimary,
      verification_package_id: this.verificationPackageSelect?.id,
      currency_id: this.form.get('currency_id')?.value
    }
    return createPayload;
  }

  buildUpdateVerificationPackagePayload() {
    let primaryStatusId = 0;
    if (this.selectedVerificationPackage.primaryStatus === "Primary") {
      primaryStatusId = 1;
    }

    let updatePayload = {
      id: this.selectedVerificationPackage.id,
      verification_package_id: this.verificationPackageSelect?.id ? this.verificationPackageSelect?.id : this.data.verificationPackageDetail.verification_package_id,
      organization_id: this.data.organizationId,
      currency_id: this.form.get('currency_id')?.value,
      price: this.form.get('price')?.value ? this.form.get('price')?.value : 0,
      is_active: 1,
      is_primary: primaryStatusId,
      updated_date: new Date()
    }
    return updatePayload;
  }

  canAssignVerificationPackage(): boolean {
    return (
      this.form.valid
    )
  }

  cancelAddVerificationPackage() {
    this.resetForm();
    this.dialogRef.close(false);
  }

  resetForm() {
    this.form.reset();
    this.verificationPackageSelect = undefined;
  }

  displayVerifPackageFn(verifPackage: any): any {
    if (verifPackage) {
      return verifPackage.id + ' - ' + verifPackage.name + ' - ' + verifPackage.description;
    } else {
      return '';
    }
  }
}